import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PRODUCT, ADD_TO_CART } from '../../apollo/queries';
import { useLoading } from '../../contexts/LoadingContext';
import { useAuth } from '../../admin/AuthContext';
import ImageWithPlaceholder from '../../components/ImageWithPlaceholder';
import LoginPromptModal from '../../components/LoginPromptModal';
import CartSuccessModal from '../../components/CartSuccessModal';
import '../css/ProductDetailPage.css';

const ProductDetailPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const productId = searchParams.get('id');
    const { startLoading, stopLoading, updateMessage } = useLoading();
    const { isAuthenticated } = useAuth();
    
    // 상태 관리
    const [selectedOption, setSelectedOption] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [showCartSuccessModal, setShowCartSuccessModal] = useState(false);

    // GraphQL 쿼리 및 뮤테이션
    const { data, loading, error } = useQuery(GET_PRODUCT, {
        variables: { id: productId },
        skip: !productId
    });

    const [addToCart, { loading: addingToCart }] = useMutation(ADD_TO_CART, {
        onCompleted: (data) => {
            setShowCartSuccessModal(true);
        },
        onError: (error) => {
            console.error('장바구니 추가 에러:', error);
            alert('로그인 후 사용해주십시오 .');
        }
    });

    // 로딩 상태 관리
    useEffect(() => {
        if (loading) {
            startLoading('제품 정보를 불러오는 중...');
        } else if (addingToCart) {
            updateMessage('장바구니에 추가하는 중...');
        } else {
            stopLoading();
        }
    }, [loading, addingToCart, startLoading, stopLoading, updateMessage]);

    // 제품 데이터 가져오기
    const product = data?.product;
    const availableOptions = product?.options || [];

    // 도금 옵션이 있으면 첫 번째 옵션을 기본값으로 설정 (모든 렌더에서 동일한 순서로 호출되도록 early return 전에 배치)
    useEffect(() => {
        if (availableOptions.length > 0 && !selectedOption) {
            setSelectedOption(availableOptions[0]);
        }
    }, [availableOptions, selectedOption]);

    // 수량 변경 핸들러
    const handleQuantityChange = (delta) => {
        setQuantity(prev => Math.max(1, prev + delta));
    };


    // 장바구니 추가 핸들러
    const handleAddToCart = () => {
        // 로그인 체크
        if (!isAuthenticated()) {
            setShowLoginPrompt(true);
            return;
        }
        
        addToCart({
            variables: {
                productId: productId,
                quantity: quantity
            }
        });
    };

    // 로딩 상태
    if (loading) {
        return (
            <div className="product-detail-page">
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div>상품 정보를 불러오는 중입니다...</div>
                </div>
            </div>
        );
    }

    // 에러 상태
    if (error) {
        return (
            <div className="product-detail-page">
                <div style={{ textAlign: 'center', padding: '50px', color: '#ff4444' }}>
                    <div>상품 정보를 불러오는 중 오류가 발생했습니다.</div>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="product-detail-page">
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div>상품을 찾을 수 없습니다.</div>
                </div>
            </div>
        );
    }

    // 이미지 파싱
    let images = [];
    if (product.images) {
        try {
            if (typeof product.images === 'string') {
                const cleanedImages = product.images.trim();
                
                // 빈 문자열이나 빈 배열 체크
                if (cleanedImages && cleanedImages !== '[]' && cleanedImages !== '') {
                    if (cleanedImages.startsWith('[')) {
                        images = JSON.parse(cleanedImages);
                    } else if (cleanedImages.includes('http') || cleanedImages.includes('/')) {
                        // 단일 URL 문자열인 경우
                        images = [cleanedImages];
                    }
                }
            } else if (Array.isArray(product.images)) {
                images = product.images;
            }
            
            // 유효한 이미지 URL만 필터링
            images = images.filter(url => url && typeof url === 'string' && url.trim() !== '');
            
        } catch (e) {
            console.error('이미지 파싱 에러:', e);
            images = [];
        }
    }

    // 이미지 설정
    const mainImage = images.length > 0 ? images[0] : null;
    const additionalImages = images.slice(1, 4); // 2~4번째 이미지

    // 파일 다운로드/미리보기 핸들러
    const handleFileAction = (fileType) => {
        if (fileType === 'pdf') {
            if (product.pdfFiles && product.pdfFiles.length > 0) {
                // 첫 번째 PDF 파일을 새 탭에서 미리보기
                const pdfFile = product.pdfFiles[0];
                window.open(pdfFile.url, '_blank');
            } else {
                alert('등록된 PDF 파일이 없습니다.');
            }
        } else if (fileType === 'cad') {
            if (product.cadFiles && product.cadFiles.length > 0) {
                // 첫 번째 CAD 파일 다운로드
                const cadFile = product.cadFiles[0];
                downloadFile(cadFile.url, cadFile.name);
            } else {
                alert('등록된 CAD 파일이 없습니다.');
            }
        }
    };

    // 파일 다운로드 헬퍼 함수
    const downloadFile = (url, filename) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'download';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="product-detail-page">
            <div className="detail-breadcrumb">
                <div className="breadcrumb-content">
                    <a href="/">홈</a>
                    <span>{'>'}</span>
                    <a href="/shop">모선 부스바</a>
                    <span>{'>'}</span>
                    <a href="/shop">전체보기</a>
                    <span>{'>'}</span>
                    <span className="current">{product.name}</span>
                </div>
            </div>

            <main className="detail-main-content">
                <div className="detail-title-section">
                    <h1>{product.name}</h1>
                </div>

                <div className="detail-content-section">
                    <div className="product-gallery">
                        <div className="gallery-thumbnails">
                            {images.slice(0, 2).map((image, index) => (
                                <div key={index} className="thumbnail-wrapper">
                                    <ImageWithPlaceholder src={image} alt={`썸네일 ${index + 1}`} className="thumbnail" />
                                </div>
                            ))}
                        </div>
                        <div className="main-image-container">
                            <ImageWithPlaceholder src={mainImage} alt={product.name} className="main-image" />
                        </div>
                    </div>

                    <div className="product-details">
                        <div className="detail-options">
                            <div className="option-row">
                                <span className="option-label">제품 번호</span>
                                <span className="option-value">{product.productCode || product.id}</span>
                            </div>
                            <div className="option-row">
                                <span className="option-label">부스바 사이즈</span>
                                <span className="option-value">{product.size || '미입력'}</span>
                            </div>
                            {availableOptions.length > 0 && (
                                <div className="option-row">
                                    <span className="option-label">도금 옵션</span>
                                    <select 
                                        className="option-select"
                                        value={selectedOption}
                                        onChange={(e) => setSelectedOption(e.target.value)}
                                    >
                                        {availableOptions.map((option, index) => (
                                            <option key={index} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="option-row">
                                <span className="option-label">수량</span>
                                <div className="quantity-selector">
                                    <button onClick={() => handleQuantityChange(-1)}>-</button>
                                    <span>{quantity}</span>
                                    <button onClick={() => handleQuantityChange(1)}>+</button>
                                </div>
                            </div>
                        </div>

                        <div className="detail-options-divider"></div>

                        <div className="product-actions-container">
                            <button 
                                className="add-to-cart-btn" 
                                onClick={handleAddToCart}
                                disabled={addingToCart}
                            >
                                {addingToCart ? '처리 중...' : '장바구니 담기'}
                            </button>

                            <div className="info-box">
                                <p>장바구니에 담아 견적을 요청할 수 있습니다.<br />접수된 견적문의는 담당자 확인 후 신속하고 친절하게 안내해 드리겠습니다.</p>
                            </div>

                            <div className="document-buttons">
                                <button className="doc-btn pdf-btn" onClick={() => handleFileAction('pdf')}>
                                    <span>참고 도면</span>
                                    <span>PDF</span>
                                </button>
                                <button className="doc-btn cad-btn" onClick={() => handleFileAction('cad')}>
                                    <span>참고 도면</span>
                                    <span>CAD</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 로그인 필요 팝업 */}
                <LoginPromptModal
                    isOpen={showLoginPrompt}
                    onClose={() => setShowLoginPrompt(false)}
                    onLogin={() => {
                        setShowLoginPrompt(false);
                        window.dispatchEvent(new CustomEvent('openLoginPanel'));
                    }}
                />

                {/* 장바구니 추가 성공 모달 */}
                <CartSuccessModal
                    isOpen={showCartSuccessModal}
                    onClose={() => setShowCartSuccessModal(false)}
                    onContinueShopping={() => setShowCartSuccessModal(false)}
                    onViewCart={() => {
                        setShowCartSuccessModal(false);
                        navigate('/cart');
                    }}
                />


                {/* 2~4번째 이미지들을 full width로 표시 */}
                {additionalImages.map((image, index) => (
                    <div key={index} className="detail-image-full-width">
                        <ImageWithPlaceholder src={image} alt={`상세 이미지 ${index + 2}`} />
                    </div>
                ))}

                {/* 추가 이미지 섹션 */}
                {mainImage && (
                    <div className="product-detail-image-section">
                        <ImageWithPlaceholder src={mainImage} alt={product.name} className="detail-main-image" />
                    </div>
                )}

                <div className="product-extra-details">
                    <div className="purchase-info-section">
                        <h3>구매 방법</h3>
                        <div className="purchase-steps">
                            <p className="purchase-desc">
                                귀사 견적이 실수 없이 발주 될 수 있도록, 간단한 메일 발주 방식을 운영 중입니다.
                                <br />
                                견적서를 토대로 구매 메일 요청해 주시면 빠르게 주문가능 합니다.
                            </p>
                            <div className="contact-methods">
                                <div className="contact-item">
                                    <img src="/polygon-100.svg" alt="메일" className="contact-icon" />
                                    <div className="contact-info">
                                        <span className="contact-label">메일 :</span>
                                        <a href="mailto:info@busbarworld.com" className="contact-value">info@busbarworld.com</a>
                                    </div>
                                </div>
                                <div className="contact-item">
                                    <img src="/polygon-101.svg" alt="전화" className="contact-icon" />
                                    <div className="contact-info">
                                        <span className="contact-label">전화 :</span>
                                        <span className="contact-value">010-1234-5678</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="purchase-steps-indicator">
                            <div className="step-indicator">
                                <div className="step-indicator-bg"></div>
                                <div className="step-indicator-fill"></div>
                                <div className="step-item">
                                    <div className="step-circle active"></div>
                                    <span>견적안내 /확인</span>
                                </div>
                                <div className="step-item">
                                    <div className="step-circle active"></div>
                                    <span>메일 발주</span>
                                </div>
                                <div className="step-item">
                                    <div className="step-circle active"></div>
                                    <span>제품 제작</span>
                                </div>
                            </div>
                        </div>

                        <h3>제품스펙</h3>
                        <div className="specs-content">
                            <div className="specs-labels">
                                <div>원산지</div>
                                <div>규격</div>
                                <div>재질</div>
                                <div>권장 용량</div>
                                <div>순도</div>
                                <div>전도율</div>
                            </div>
                            <div className="specs-values">
                                <div>한국</div>
                                <div>{product.size || '6T X 30'}</div>
                                <div>동 (니켈도금)</div>
                                <div>200A</div>
                                <div>Cu 99.2%</div>
                                <div>98%</div>
                            </div>
                        </div>
                    </div>

                    <div className="shipping-exchange-section">
                        <div className="shipping-info-section">
                            <h3>배송 안내</h3>
                            <div className="info-content">
                                배송지역도서 산간지역은 배송이 제한될 수 있습니다.
                                <br />
                                (단, 제주도 등 일부 도서/산간 지역은 별도 배송비가 추발생할 수 있으며, 대량주문의 경우 추가 배송비가 발생할 수 있습니다.)
                                <br />
                                <br />
                                제작 완료 후 평균 3일 이내 배송 (일, 공휴일 제외)
                                <br />
                                단, 도서/산간지역은 배송일이 추가적으로 소요될 수 있으며, 상품 보유현황에 따라 배송일이 지연될 수 있습니다.
                                <br />
                                또한 인수고객과의 통화불가, 수령지 주 소가 불명확할 경우 사전 연락(전화 또는 문자 등) 후 배송이 지연되거나 주문이 취소될 수 있습니다.
                                <br />
                                <br />
                                화물배송은 대신 화물 / 경동 화물로 배송됩니다.
                            </div>
                        </div>

                        <div className="exchange-info-section">
                            <h3>교환 / 반품 안내</h3>
                            <div className="info-content">
                                상품 문의 및 상품 리뷰를 통해 취소나 환불, 반품은 처리되지 않습니다.
                                <br />
                                주문제작의 경우 반품이 불가하며
                                <br />
                                요청한 도면과 다를 경우에 교환 가능합니다.
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ProductDetailPage; 